import { useEffect, useMemo, useState } from 'react'
import { check, loadClassifications } from '../classifications'
import type { Classifications } from '../classifications'

/**
 * The page's one interactive moment: the Leviticus 11 check, running for real.
 *
 * Not a video and not a screenshot. This is the same `check()` the recipe form uses, against the
 * same published list, so a stranger can type "pork gelatin" and watch it refuse, then type
 * "beef gelatin" and watch it resolve. That distinction is the whole argument for the product
 * and no competitor can copy-paste it, because it needs the authored list behind it.
 *
 * It reads the server list only. There is no bundled fallback and no demo fixture: a second list
 * kept here to make the marketing work would be exactly the drift the product refuses. When the
 * list has not been published yet the panel says so plainly rather than performing.
 */

const SUGGESTIONS = ['pork gelatin', 'beef gelatin', 'graham crackers', 'steamed clams']

export function CleanCheckDemo() {
  const [classifications, setClassifications] = useState<Classifications | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [text, setText] = useState('beef gelatin')

  useEffect(() => {
    void loadClassifications().then((value) => {
      setClassifications(value)
      setLoaded(true)
    })
  }, [])

  const result = useMemo(
    () => check([text], classifications?.ingredients ?? []),
    [text, classifications],
  )

  const verdictWord =
    result.verdict === 'CLEAN'
      ? 'Clean'
      : result.verdict === 'NEEDS_SOURCE'
        ? 'Say where it came from'
        : result.verdict === 'REJECTED'
          ? 'Not this one'
          : 'Not checked'

  return (
    <div className="demo">
      <label className="demo-label" htmlFor="demo-input">
        Type an ingredient
      </label>
      <input
        id="demo-input"
        className="demo-input"
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        autoComplete="off"
        spellCheck={false}
      />

      <div className="demo-tries">
        {SUGGESTIONS.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            className={text === suggestion ? 'try try-on' : 'try'}
            onClick={() => setText(suggestion)}
          >
            {suggestion}
          </button>
        ))}
      </div>

      <div className={`demo-out demo-${result.verdict.toLowerCase()}`} aria-live="polite">
        {!loaded ? (
          <p className="demo-verdict">Reading the list</p>
        ) : !classifications ? (
          <p className="demo-note">
            Couldn't reach the published list, so nothing here has been checked. Covellah never
            calls a food clean on a guess.
          </p>
        ) : (
          <>
            <p className="demo-verdict">{verdictWord}</p>
            {result.findings.length > 0 ? (
              result.findings.map((finding) => (
                <div key={finding.ruleId} className="demo-finding">
                  <p className="demo-reason">{finding.reason}</p>
                  {finding.references.length > 0 && (
                    <p className="demo-ref">{finding.references.join(' · ')}</p>
                  )}
                </div>
              ))
            ) : (
              <p className="demo-note">Nothing in Leviticus 11 speaks against it.</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
